/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useMemo } from "react";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { TableProps } from "interfaces";
import { ResourceInterface, ProviderInterface } from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import { EntityPermissions, PermissionsUser } from "types";

/**
 * Extended resource interface with new resource indicator.
 * 
 * @interface ExtendedResourceInterface
 * @extends {Partial<ResourceInterface>}
 * @property {boolean} [isNew] - Flag indicating if resource is newly added (not yet saved)
 */
interface ExtendedResourceInterface extends Partial<ResourceInterface> {
  isNew?: boolean;
}

/**
 * Properties for the ResourceListModalTable component.
 * 
 * @interface ResourceTableProps
 * @extends {TableProps<ExtendedResourceInterface>}
 * @property {ProviderInterface[]} doctorList - List of available doctors/providers
 * @property {Function} onRemoveResource - Callback to remove resource by index
 * @property {boolean} [canDelete] - Whether delete functionality is enabled
 */
interface ResourceTableProps extends TableProps<ExtendedResourceInterface> {
  doctorList: ProviderInterface[];
  onRemoveResource: (index: number) => void;
  canDelete?: boolean;
}

/**
 * Styled table row with visual indication for newly added resources.
 * Green highlight for new resources with hover effect.
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&.new-resource": {
    backgroundColor: theme.palette.success.light + "20",
    "&:hover": {
      backgroundColor: theme.palette.success.light + "40",
    },
  },
}));

/**
 * Resource list modal table component for displaying resources with new/existing status.
 * Used in modal dialogs for resource selection and management.
 * 
 * Features:
 * - Visual distinction between new and existing resources
 * - Status indicator (NEW/EXISTING) with color coding
 * - Bold text and green highlighting for new resources
 * - Display resource name, type, assigned doctor, capacity
 * - Remove resource button with permission control
 * - Delete functionality can be toggled
 * - Doctor name resolution from provider list
 * 
 * Visual Indicators:
 * - New resources: Green badge, bold text, green highlight background
 * - Existing resources: Gray badge, normal text
 * 
 * Columns:
 * - Status: NEW or EXISTING badge
 * - Name: Resource name (bold if new)
 * - Type: Resource type
 * - Doctor: Assigned provider name
 * - Capacity: Resource capacity
 * - Actions: Remove button (permission-based)
 * 
 * @param {ResourceTableProps} props - Component properties
 * @param {ExtendedResourceInterface[]} props.modelList - Array of resources to display
 * @param {Function} props.setModelList - Callback to update resource list
 * @param {ProviderInterface[]} props.doctorList - List of doctors for name resolution
 * @param {Function} props.onRemoveResource - Callback when removing resource
 * @param {boolean} [props.canDelete=false] - Enable/disable delete functionality
 * 
 * @returns {JSX.Element} Resource table with new/existing visual indicators
 */
const ResourceTable: React.FC<ResourceTableProps> = ({
  modelList,
  setModelList,
  doctorList,
  onRemoveResource,
  canDelete = false,
  ...props
}) => {
  const { t } = useTranslation();

  const getDoctorName = (providerId?: string) => {
    if (!providerId) return "";
    const doctor = doctorList.find((d) => d.id === providerId);
    return doctor ? `${doctor.person.name} ${doctor.person.lastname}` : "";
  };

  const getUITable = (list: ExtendedResourceInterface[]) => ({
    columns: [
      {
        Header: t("common.status"),
        accessor: "status",
        sortIndicatorType: "az",
        Cell: ({ row }: any) => {
          const resource: ExtendedResourceInterface = row.original;
          return (
            <Box
              sx={{
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "bold",
                textAlign: "center",
                backgroundColor: resource.isNew ? "#4caf50" : "#e0e0e0",
                color: resource.isNew ? "white" : "#666",
              }}
            >
              {resource.isNew ? t("resourceList.new") : t("resourceList.existing")}
            </Box>
          );
        },
      },
      {
        Header: t("resourceForm.name"),
        accessor: "name",
        sortIndicatorType: "az",
        Cell: ({ value, row }: { value: any; row: any }) => {
          const resource: ExtendedResourceInterface = row.original;
          return (
            <Box
              sx={{
                fontWeight: resource.isNew ? "bold" : "normal",
                color: resource.isNew ? "#2e7d32" : "inherit",
              }}
            >
              <DefaultCell value={value || ""} />
            </Box>
          );
        },
      },
      {
        Header: t("resourceForm.code"),
        accessor: "code",
        sortIndicatorType: "numeric",
        Cell: ({ value, row }: { value: any; row: any }) => {
          const resource: ExtendedResourceInterface = row.original;
          return (
            <Box
              sx={{
                fontWeight: resource.isNew ? "bold" : "normal",
                color: resource.isNew ? "#2e7d32" : "inherit",
              }}
            >
              <DefaultCell value={value || ""} />
            </Box>
          );
        },
      },
      {
        Header: t("facilityForm.phone"),
        accessor: "phone",
        sortIndicatorType: "numeric",
        Cell: ({ value, row }: { value: any; row: any }) => {
          const resource: ExtendedResourceInterface = row.original;
          return (
            <Box
              sx={{
                fontWeight: resource.isNew ? "bold" : "normal",
                color: resource.isNew ? "#2e7d32" : "inherit",
              }}
            >
              <DefaultCell value={value || "-"} />
            </Box>
          );
        },
      },
      {
        Header: t("appointmentForm.doctorProvider"),
        accessor: "defaultAppointmentProvider.id",
        sortIndicatorType: "az",
        Cell: ({ row }: any) => {
          const resource: ExtendedResourceInterface = row.original;
          const doctorName = getDoctorName(resource.defaultAppointmentProvider?.id);
          return (
            <Box
              sx={{
                fontWeight: resource.isNew ? "bold" : "normal",
                color: resource.isNew ? "#2e7d32" : "inherit",
              }}
            >
              <DefaultCell value={doctorName || "-"} />
            </Box>
          );
        },
      },
      {
        Header: t("common.actions"),
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const index = row.index;
          const resource: ExtendedResourceInterface = row.original;

          return (
            <>
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].DELETE]}
              >
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => onRemoveResource(index)}
                  title={
                    resource.isNew
                      ? t("resourceList.removeFromList")
                      : t("resourceList.deleteFromDatabase")
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </PermissionWrapper>
            </>
          );
        },
      },
    ],
    rows: list,
    customRowComponent: ({ row, children }: any) => {
      const resource: ExtendedResourceInterface = row.original;
      return (
        <StyledTableRow className={resource.isNew ? "new-resource" : ""}>{children}</StyledTableRow>
      );
    },
  });

  const dataTableData = useMemo(() => getUITable(modelList), [modelList, t, doctorList, canDelete]);

  return (
    <Box width="100%">
      <DataTable {...props} table={dataTableData} canSearch canSearchPlaceholder={t("resourceTable.searchResource")}/>
    </Box>
  );
};

export default ResourceTable;
